import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { ReactSelect, SpinnerLoader } from "@services/ui-components";
import { WearhousesModalProps } from "../../Warehouse/modals/modalsUtils";
import {
    useUpdateStorageProductHistoryMutation,
    useUpdateStorageProductRelationMutation,
} from "@me-team/host/main/warehouses_goods/graphql/warehouses_goods.hooks";
import {
    CompanyAccess,
    StorageProductRelationActionType,
    StorageProductRelationHistoryAction,
    UpdateStorageProductRelationInput,
} from "@me-team/host/main/graphql/types";
import { Option } from "../../Products/types";
import { writeOffSchema } from "../constants";
import QuantityInput from "../../AddNewProductList/AddProductListModal/QuantityInput";
import ModalProductInfo from "../components/ModalProductInfo";
import moment from "moment";
import CustomCreatableReactSelect from "@services/ui-components/src/select/CustomCreatableReactSelect";
import InvalidQuantityModal from "./InvalidQuantityModal";
import { useParams } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const WriteOffModal: React.FC<WearhousesModalProps> = ({
    show,
    handleModal,
    refetch,
    warehouse,
    chosenProduct,
    writeOffReasons,
    toggleShowToast,
    isEditMode = false,
    centered = true,
    dialogClassName,
    dataTour,
    canEditOperation,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canWriteOffProduct = useRoleAccess(CompanyAccess.WarehouseProductWriteOff);
    const canSubmit = isEditMode ? Boolean(canEditOperation) : canWriteOffProduct;
    const contentRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<FormikProps<UpdateStorageProductRelationInput>>(null);
    const isDisabled = warehouse?.default;
    const initialValues: UpdateStorageProductRelationInput = {
        quantity: isEditMode ? (chosenProduct?.operationQuantity ?? null) : null,
        unit: isEditMode && chosenProduct?.operationUnit?.id ? chosenProduct.operationUnit.id : null,
        writeOffReason: isEditMode && chosenProduct?.writeOffReason?.name ? chosenProduct.writeOffReason.name : null,
        action: StorageProductRelationActionType.WriteOff,
    };
    const [unitOfMeasurements, setUnitOfMeasurements] = useState([]);
    const [isShowWriteOffModal, setIsShowWriteOffModal] = useState(false);
    const [isShowQuantityModal, setIsShowQuantityModal] = useState(false);
    const isAllowNegative = useRef(false);
    const tutorialActive = isWarehouseTutorialActive();

    useEffect(() => {
        if (contentRef.current && dataTour) {
            contentRef.current.setAttribute("data-tour", dataTour);
        }
    }, [dataTour, show]);

    const [updateStorageProductRelation] = useUpdateStorageProductRelationMutation({
        context: {
            errorType: "local",
        },
    });

    const [updateStorageProductHistory] = useUpdateStorageProductHistoryMutation({
        context: {
            errorType: "local",
        },
    });
    const handleSubmit = async (
        values: typeof initialValues,
        { setFieldError }: FormikHelpers<typeof initialValues>
    ) => {
        if (!canSubmit) return;

        try {
            if (isEditMode) {
                if (currentBranchEmployeeId == null) return;
                const operationType =
                    chosenProduct?.historyAction === StorageProductRelationHistoryAction.BookingWriteOff
                        ? StorageProductRelationHistoryAction.BookingWriteOff
                        : StorageProductRelationHistoryAction.WriteOff;

                await updateStorageProductHistory({
                    variables: {
                        input: [
                            {
                                storageProductRelationHistoryId: +chosenProduct?.id,
                                updateStorageProductRelationHistoryInputType: {
                                    quantity: +values?.quantity,
                                    unit: values.unit,
                                    writeOffReason: values.writeOffReason,
                                },
                            },
                        ],
                        branchId: Number(branchId),
                        employeeId: Number(currentBranchEmployeeId),
                        allowNegative: isAllowNegative.current,
                        operationType,
                    },
                });
            } else {
                await updateStorageProductRelation({
                    variables: {
                        storageProductRelationId: +chosenProduct?.id,
                        input: { ...values, quantity: +values?.quantity, action: values.action },
                        branchId: Number(branchId),
                    },
                });
            }
            refetch();
            handleModal();
            tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
            tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
            toggleShowToast(t("Saved"));
        } catch (error) {
            const graphQLError = error.graphQLErrors?.[0].message;
            if (graphQLError.startsWith("Write off quantity")) {
                setFieldError(
                    "quantity",
                    t("In stock less than") +
                        " " +
                        values?.quantity +
                        unitOfMeasurements.find((i) => i.value === values?.unit)?.label
                );
            } else if (graphQLError.startsWith("Update of this operation will cause negative remnants on storage")) {
                setIsShowQuantityModal(true);
                setIsShowWriteOffModal(false);
            } else {
                console.error(error);
            }
        } finally {
            isAllowNegative.current = false;
        }
    };
    useEffect(() => {
        const optionsUnitOfMeasurements =
            chosenProduct?.product?.units?.map((unit) => ({
                value: unit.id,
                label: unit.name,
            })) || [];

        setUnitOfMeasurements(optionsUnitOfMeasurements);
    }, [chosenProduct?.product]);

    const initialWriteOffReason = isEditMode
        ? { value: initialValues.writeOffReason, label: initialValues.writeOffReason }
        : null;

    useEffect(() => {
        setIsShowWriteOffModal(show);
    }, [show]);

    const submitForm = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleConfirmQuantity = () => {
        isAllowNegative.current = true;
        setIsShowQuantityModal(false);
        setIsShowWriteOffModal(true);
        submitForm();
    };

    const handleQuantityModalClose = () => {
        setIsShowQuantityModal(false);
        setIsShowWriteOffModal(true);
    };

    return (
        <>
            <InvalidQuantityModal
                show={isShowQuantityModal}
                handleHide={handleQuantityModalClose}
                handleConfirm={handleConfirmQuantity}
            />
            <Modal
                size="lg"
                show={show}
                onHide={() => {
                    tutorialActive && moveStepInLocalStorage(StepDirection.PREV);
                    tutorialActive && window.dispatchEvent(new Event("warehouseStepChanged"));
                    handleModal();
                }}
                className={`${isShowWriteOffModal ? "" : "d-none"}`}
                centered={centered}
                dialogClassName={`${dialogClassName ? dialogClassName : ""}`}
                onEntered={() => {
                    tutorialActive && requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
                }}
            >
                <div ref={contentRef}>
                    <Modal.Header className="border-0" closeButton>
                        <h5 className="m-0">{t(isEditMode ? "Edit product write-off" : "Write-off the product")}</h5>
                    </Modal.Header>
                    <Modal.Body>
                        <ModalProductInfo
                            date={chosenProduct?.date || moment().format("YYYY-MM-DD HH:mm:ss")}
                            productName={chosenProduct?.product?.name}
                            brand={chosenProduct?.product?.brand}
                            categoryName={chosenProduct?.product?.productCategoryNew?.name}
                            sku={chosenProduct?.product?.sku}
                            barcode={chosenProduct?.product?.barcode}
                            unitGroupName={chosenProduct?.product?.unitGroup?.name}
                            remnants={chosenProduct?.remnantsForRender || chosenProduct?.quantityForRender}
                        />
                        <Formik
                            initialValues={initialValues}
                            validationSchema={writeOffSchema(t)}
                            onSubmit={handleSubmit}
                            validateOnChange={false}
                            enableReinitialize
                            innerRef={formRef}
                        >
                            {({ setFieldValue, isSubmitting, values }) => (
                                <Form>
                                    <Row className={"mx-0 mt-3"}>
                                        <Col sm={3} className="pr-0">
                                            <label
                                                htmlFor="createWarehouseModal-name-field"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                                style={{ maxHeight: "49px" }}
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Quantity for write-off")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <QuantityInput
                                                name="quantity"
                                                id="addsProduct-quantity-field"
                                                placeholder={t("Add product amount")}
                                                maxLength={9}
                                                isAllowZero={isEditMode}
                                            />
                                            <ErrorMessage
                                                name="quantity"
                                                component="div"
                                                className="fs-7 m-0 p-1 text-danger"
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="pt-3 mx-0">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="unit"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                                style={{ maxHeight: "49px" }}
                                            >
                                                <span className="text-dark fw-bold fs-7 required-label">
                                                    {t("Unit of measurements")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <ReactSelect
                                                id="addProduct-unit-field"
                                                name="unit"
                                                placeholder={t("Select an unit")}
                                                options={unitOfMeasurements}
                                                value={unitOfMeasurements?.find(
                                                    (option: Option) => option.value === values.unit
                                                )}
                                                onChange={(selectedOption: Option) =>
                                                    setFieldValue("unit", selectedOption.value, true)
                                                }
                                                isSearchable={false}
                                            />

                                            <ErrorMessage
                                                name="unit"
                                                component="div"
                                                className="fs-7 m-0 p-1 text-danger"
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="pt-3 mb-3 mx-0">
                                        <Col sm={3} className="d-flex pr-0">
                                            <label
                                                htmlFor="writeOffReason"
                                                className="d-flex align-items-center mb-0 h-100 text-dark form-label required text-break mb-2 mb-sm-0"
                                                style={{ maxHeight: "49px" }}
                                            >
                                                <span className="text-dark fw-bold fs-7  required-label">
                                                    {t("Write-off reason")}
                                                </span>
                                            </label>
                                        </Col>
                                        <Col sm={9}>
                                            <CustomCreatableReactSelect
                                                optionsArray={writeOffReasons}
                                                placeholder={t("Select write-off reason")}
                                                setField={setFieldValue}
                                                fieldName="writeOffReason"
                                                value="name"
                                                isTooltip={false}
                                                name={"writeOffReason-field"}
                                                id={"addProduct-writeOffReason-field"}
                                                disabled={isDisabled}
                                                initialValue={initialWriteOffReason}
                                                maxLength={100}
                                                createOptionLabel={t("Create a write-off reason")}
                                                noOptionsText={t("Start typing the reason for the write-off")}
                                                createOnBlur={true}
                                            />
                                            <ErrorMessage
                                                name="writeOffReason"
                                                component="div"
                                                className="fs-7 m-0 p-1 text-danger"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="d-flex justify-content-end pt-6">
                                        <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                            <Button
                                                variant="outline-primary"
                                                className="fw-normal custom-btn rounded-1"
                                                onClick={() => {
                                                    tutorialActive && moveStepInLocalStorage(StepDirection.PREV);
                                                    tutorialActive &&
                                                        window.dispatchEvent(new Event("warehouseStepChanged"));
                                                    handleModal();
                                                }}
                                            >
                                                {t("Cancel")}
                                            </Button>
                                        </Col>
                                        <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                className="fw-bold custom-btn rounded-1"
                                                disabled={isSubmitting || !canSubmit}
                                            >
                                                {isSubmitting ? (
                                                    <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                                ) : (
                                                    t(isEditMode ? "Save" : "Write-Off")
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </Modal.Body>
                </div>
            </Modal>
        </>
    );
};

export default WriteOffModal;
